import axios, { AxiosRequestConfig } from 'axios';
// config
import { HOST_API_KEY } from '../config-global';

// ----------------------------------------------------------------------
export interface Params {
  [key: string]:
    | boolean
    | string
    | string[]
    | number
    | number[]
    | {
        [key: string]: string[];
      }
    | undefined;
}
const body = {};
export interface HttpParamsType<T = typeof body> {
  queryParams?: Params;
  body?: T;
  config?: AxiosRequestConfig;
}

const axiosInstance = axios.create({ baseURL: HOST_API_KEY });

axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 403 Forbidden error
    if (error.response?.status === 403) {
      // Clear local storage
      localStorage.clear();

      // Redirect to login page
      window.location.href = '/login'; // Adjust the path to match your application's login page
    }

    // Reject the error to propagate it to the caller
    return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  }
);

axiosInstance.interceptors.request.use(
  async (config) => {
    const authHeader = await getAuthorizationHeader();

    if (authHeader) {
      config.headers['Authorization'] = authHeader;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default axiosInstance;

export async function get<T>(url: string, paramsData?: HttpParamsType): Promise<T> {
  try {
    const { data } = await axiosInstance.get<T>(url, {
      params: paramsData && paramsData?.queryParams,
      ...paramsData?.config,
    });

    return data;
  } catch (error) {
    throw error;
  }
}

export async function post<T>(url: string, paramsData?: HttpParamsType): Promise<T> {
  const options = {
    body: paramsData && paramsData.body,
    params: paramsData && paramsData.queryParams,
  };

  try {
    const { data } = await axiosInstance.post<T>(url, options.body, {
      params: options && options.params,
    });

    return data;
  } catch (error) {
    throw error;
  }
}

export async function patch<T>(url: string, paramsData?: HttpParamsType): Promise<T> {
  const options = {
    params: paramsData && paramsData.queryParams,
    body: paramsData && paramsData.body,
  };

  try {
    const { data } = await axiosInstance.patch<T>(url, options.body, {
      params: options && options.params,
    });

    return data;
  } catch (error) {
    throw error;
  }
}

export async function _delete<T = void>(url: string, paramsData?: HttpParamsType): Promise<T> {
  try {
    const { data } = await axiosInstance.delete<T>(url, {
      params: paramsData && paramsData.queryParams,
      data: paramsData && paramsData.body,
    });

    return data;
  } catch (error) {
    throw error;
  }
}

async function getAuthorizationHeader() {
  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    return `Bearer ${accessToken}`;
  }

  return null;
}
